import ConfirmationDot from '@/components/ui/verification/confirmation-dot.tsx';
import { useDealContext } from '@/contexts/deal-provider';
import { useDealByShortId } from '@/hooks/use-deals-selectors';
import { pdkAxios } from '@/lib/pdk-axios';
import type { PrimaryCustomer } from '@/models/registry';
import { dealStore } from '@/models/stores/deals';
import { ConfirmationField, ConfirmationTargetType } from '@/models/types.ts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import InlineTextInput from '../../ui/inline-text-input';

const PrimaryCustomerPhoneEditor = observer(() => {
  const { currentShortId } = useDealContext();
  const deal = useDealByShortId(currentShortId);
  const dealShortId = deal?.short_id || '';
  const phone = deal?.primary_customer?.mobile_number || '';
  const customerId = deal?.primary_customer?.id || '';
  const dealId = deal?.id || '';

  const [phoneValue, setPhoneValue] = useState(phone);

  const queryClient = useQueryClient();
  const updatePrimaryCustomerPhoneMutation = useMutation({
    onMutate: async ({ primaryCustomerPhone }) => {
      dealStore.updatePrimaryCustomerMobileNumber(
        customerId,
        primaryCustomerPhone,
      );
    },
    mutationFn: async ({
      primaryCustomerPhone,
    }: {
      primaryCustomerPhone: PrimaryCustomer['mobile_number'];
    }) => {
      if (!primaryCustomerPhone) return;

      const response = await pdkAxios
        .post(`/deals/${dealId}/primary-customer/${customerId}/mobile-number`, {
          mobile_number: primaryCustomerPhone,
        })
        .catch((e) => {
          console.error('Error updating primary customer phone', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {},
    onError: (error, variables) => {
      console.error('Error updating primary customer phone', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealShortId] });
    },
  });

  const handlePrimaryCustomerPhoneChange = useCallback(
    debounce((newPrimaryCustomerPhone: string) => {
      updatePrimaryCustomerPhoneMutation.mutate({
        primaryCustomerPhone: newPrimaryCustomerPhone,
      });
    }, 1000),
    [],
  );

  useEffect(() => {
    if (phoneValue === phone) return;

    handlePrimaryCustomerPhoneChange(phoneValue);
  }, [phoneValue]);

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneValue(e.target.value);
  }, []);

  useEffect(() => {
    setPhoneValue(phone);
  }, [phone]);

  return (
    <div className="flex flex-row">
      <InlineTextInput
        key={`primary-customer-phone-editor-${dealId}`}
        placeholder="Customer Phone"
        value={phoneValue}
        size="sm"
        className="w-full!"
        wrapperClassName="w-full!"
        onChange={onChange}
      />
      <ConfirmationDot
        targetId={dealId}
        targetType={ConfirmationTargetType.Deal}
        field={ConfirmationField.CustomerPhoneNumber}
        value={observable.box(phoneValue)}
      />
    </div>
  );
});

export default PrimaryCustomerPhoneEditor;
